import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import Heading from '../forms/Heading';
import Form from '../forms/Form';
import Button from '../forms/Button';
import LoadingEllipsis from '../forms/LoadingEllipsis';
import Col from './Col';
import HamQuest from '../api/HamQuestGiver';
import { useGlobalState } from '../stateProvider/StateProvider';
import Row from './Row';
import { IsAuthorized } from '../RoleProtected';
import { generateId } from '../utility/functions';
import ModalFactory from '../Modal';
import { SCREEN_MAX_WIDTH } from '../../constants/config';

const DivWrapper = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 2em;
    width: 100%;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        flex-direction: column;
    }
`;
const Wrapper = styled(Form)`
    display: flex;
    align-items: flex-start;
    gap: 2em;
    width: 100%;

    padding-bottom: 1em;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        flex-direction: column;
    }
`;

const ControlWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1em;
    width: 20%;
    flex: 0.7;

    position: sticky;
    top: ${({ rowedTabs }) => rowedTabs ? '6.5' : '3.5'}em;
    
    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        width: 100%;
        flex: unset;
        align-items: stretch;
        position: unset;
    }
`;
const ContentWrapper = styled(Col)`
    flex: 2;

    @media (max-width: ${SCREEN_MAX_WIDTH}) {
        flex: unset;
        width: 100%;
    }
`;
const Span = styled.span`
    font-size: 0.8em;
    color: ${({ theme }) => theme.gray};
`;
const Error = styled.div`
    font-size: 0.7em;
    padding: 0.2em 0.5em;
    color: ${({ theme }) => theme.error};
`

const FormSetting = ({ children, ...props }) => {
    const {
        state: {
            rowedTabs,
        },
    } = useGlobalState();

    const [inputs, setInputs] = useState({});
    const [fetching, setFetching] = useState(props.getInputs ? true : false);
    const [hidden, setHidden] = useState(props.hidden);
    const [isAuthorized] = useState(IsAuthorized(props.roles))

    const [error, setError] = useState(false);

    useEffect(() => {
        if (!props.getInputs) return;
        if (hidden) return;

        HamQuest.get(props.getInputs)
            .then(res => {
                if (!res) {
                    setError(true)
                } else {
                    if (props.callback)
                        res = props.callback(res)
                    setInputs(res)
                    setFetching(false)
                }
            })
            .catch(ex => {
                console.log(ex)
                ex.title = `Error getting ${props.label}`
                setError(
                    <Col>
                        <b>{ex.title}</b>
                        <span>{ex.error?.errorMessage || 'An Error has occurred.'}</span>
                    </Col>
                )
                ModalFactory.catch(ex)
                setFetching(false)
            })
    }, [hidden])

    if (error) return error || 'An error has ocurred.'

    if (props.wrapper) {
        return (
            <DivWrapper
                {...props}
            >
                <ControlWrapper>
                    <Col>
                        <Heading>{props.label}</Heading>
                        {hidden && <Button onClick={() => setHidden(false)}>Show Options</Button>}
                    </Col>
                    {props.info && <Span>{props.info}</Span>}
                    {props.controls?.map(e => <Fragment key={generateId()}>{e}</Fragment>)}
                </ControlWrapper>
                <ContentWrapper>
                    {!hidden && children}
                </ContentWrapper>
            </DivWrapper>
        )
    }

    if (props.localForm) {
        return (
            <Wrapper
                row
                hideEdit
                hideError
                inputs={inputs}
                {...props}
            >
                <ControlWrapper>
                    <Col>
                        <Heading>{props.label}</Heading>
                        {hidden && <Button onClick={() => setHidden(false)}>Show Options</Button>}
                    </Col>
                    {props.info && <Span>{props.info}</Span>}
                    {props.controls?.map(e => <Fragment key={generateId()}>{e}</Fragment>)}
                </ControlWrapper>
                <ContentWrapper>
                    {!hidden && children}
                </ContentWrapper>
            </Wrapper>
        )
    }

    return (
        <Wrapper
            row
            hideEdit
            hideError
            isFormSetting
            inputs={inputs}
            {...props}
        >
            {(context) => (
                <>
                    <ControlWrapper rowedTabs={rowedTabs}>
                        <Col>
                            <Heading icon={!hidden && fetching ? 'spinner' : null} right>{props.label}</Heading>
                            {hidden && <Button onClick={() => setHidden(false)}>Show Options</Button>}
                        </Col>
                        {props.info && <Span>{props.info}</Span>}
                        {props.controls?.map(e => <Fragment key={generateId()}>{e}</Fragment>)}

                        <Row wrap>
                            {!hidden && props.editable && !props.displayForm && isAuthorized ?
                                context.editing ? (
                                    <Button
                                        icon={'edit'}
                                        onClick={() => context.setEditing(false)}
                                        label='Edit'
                                    />
                                ) : (
                                    <Button
                                        icon={'times'}
                                        color={'error'}
                                        disabled={context.submitting}

                                        onClick={() => {
                                            context.setEditing(true)
                                            context.setValues(context.initial)
                                        }}
                                        label='Cancel'
                                    />
                                ) : null
                            }
                            {
                                ((props.editable && !context.editing) || props.showSubmit) && (
                                    <Button
                                        disabled={context.submitting || context.dirty}
                                        type='submit'
                                        icon={context.submitting ? 'spinner' : null}
                                        isSubmitButton
                                    >
                                        {context.submitting ? (props.submitting || 'Submitting') : (props.text || 'Submit')}
                                        {context.submitting && <LoadingEllipsis />}
                                    </Button>
                                )}
                        </Row>
                        {context.formError && <Error>{context.formError}</Error>}
                    </ControlWrapper>
                    <ContentWrapper>
                        {!hidden && (typeof children === 'function' ? children(context) : children)}
                    </ContentWrapper>
                </>
            )}
        </Wrapper>
    );
}

export default FormSetting;
